import { useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { gql, type Reference, type StoreObject } from '@apollo/client';
import { useCreateMerchantMutation } from '../../../../__generated__/graphql';
import { useToasts } from '../../../../../../context/Toast';

type UseMerchantCreationReturn = {
  createMerchant: (
    merchantName: string,
  ) => Promise<{ id: string; name: string } | null>;
  isCreatingMerchant: boolean;
};

export const useMerchantCreation = (): UseMerchantCreationReturn => {
  const { t } = useTranslation();
  const { addError } = useToasts();
  const [createMerchantMutation, { loading: isCreatingMerchant }] =
    useCreateMerchantMutation({
      update(cache, { data }) {
        const merchant = data?.expenses?.createMerchant?.merchant;
        if (merchant) {
          cache.modify({
            fields: {
              merchants(
                existingMerchants:
                  | Reference
                  | ReadonlyArray<Reference | StoreObject> = [],
                { readField },
              ) {
                if (!Array.isArray(existingMerchants)) {
                  return existingMerchants;
                }
                const merchantExists = existingMerchants.some(
                  (ref) => readField<string>('Id', ref) === merchant.Id,
                );
                if (merchantExists) {
                  return existingMerchants;
                }
                const newMerchantRef = cache.writeFragment({
                  data: merchant,
                  fragment: gql`
                    fragment NewMerchant on Merchant {
                      Id
                      Name
                    }
                  `,
                });
                if (!newMerchantRef) {
                  return existingMerchants;
                }
                return [...existingMerchants, newMerchantRef];
              },
            },
          });
        }
      },
    });

  const createMerchant = useCallback(
    async (
      merchantName: string,
    ): Promise<{ id: string; name: string } | null> => {
      try {
        const result = await createMerchantMutation({
          variables: {
            input: {
              name: merchantName,
            },
          },
        });

        const merchant = result.data?.expenses?.createMerchant?.merchant;
        const error = result.data?.expenses?.createMerchant?.error;

        if (error || !merchant?.Id) {
          addError(error || t('expenses.form.merchantCreationFailed'));
          return null;
        }

        return {
          id: merchant.Id,
          name: merchant.Name || merchantName,
        };
      } catch (err) {
        addError(t('expenses.form.merchantCreationFailed'));
        return null;
      }
    },
    [createMerchantMutation, addError, t],
  );

  return {
    createMerchant,
    isCreatingMerchant,
  };
};
